/* eslint-disable no-nested-ternary */
import * as vp from 'vopay';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../BankAccount.module.scss';

const { DetailsCard, Box, SubTitle, Table, Button, Description, Tooltip, Icon } = vp;

type TablePropsHandlers = {
  personalInformation: () => vp.TableProps;
  bankAccountInformation: () => vp.TableProps;
  addressInformation: () => vp.TableProps;
  accountInformation: () => vp.TableProps;
  defaultInformation: () => vp.TableProps;
  creditCardClientDetails: () => vp.TableProps;
};

type AccountDetailsProps = {
  account: {
    Country?: string;
    Type?: string;
    ABARoutingNumber?: string;
    AccountHolderName?: string;
    AccountNumber?: string;
    Address?: string;
    BankId?: string;
    BranchTransitNumber?: string;
    City?: string;
    ClientAccountID?: string;
    ClientControlledBankAccount?: string;
    ClientReferenceNumber?: string;
    Currency?: string;
    DateAdded?: string;
    EmailAddress?: string;
    FinancialInstitutionNumber?: string;
    FlinksAccountID?: string;
    FlinksLoginID?: string;
    InstitutionName?: string;
    InveriteRequestGUID?: string;
    IsDefault?: string;
    Phone?: string;
    PlaidAccountID?: string;
    PostalCode?: string;
    Province?: string;
    Token?: string;
    CreditCardToken?: string;
    CreditCardHolderName?: string;
    CreditCardNumber?: string;
    CreditCardExpiryYear?: string;
    CreditCardExpiryMonth?: string;
    CreditCardBrand?: string;
    RecipientName?: string;
    RecipientEmailAddress?: string;
    RecipientPhoneNumber?: string;
    ID?: string;
    IsOperationalDefault?: string;
    IsBillingDefault?: string;
    IsDefaultAccount?: string;
  };
  setDefaultBank: (token: string, type: string) => void;
  AccountListingType: 'Operational' | 'Billing';
};

type FC = FunctionComponent<AccountDetailsProps>;

const AccountDetails: FC = (props: AccountDetailsProps) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Accounts.MyBankAccounts.Details.AccountDetails',
  });
  const { t: tGlobal } = useTranslation('translations');
  const { account, AccountListingType, setDefaultBank } = props;

  return (
    <Box flx="col">
      <Box mb="md">
        <SubTitle subTitle={t('subTitle') || ''} />
      </Box>
      <DetailsCard
        content={
          <Box flx="col">
            {account.Type === 'Bank Account' && (
              <>
                <Box mb="md">
                  <Table {...getTableProps('personalInformation')} />
                </Box>
                <hr className={styles.hr} />
                <div className={styles.detailsTable}>
                  <Box mb="md" mt="md">
                    <Table {...getTableProps('bankAccountInformation')} />
                  </Box>
                </div>
              </>
            )}
            {account.Type !== 'Bank Account' && (
              <Box mb="md">
                <div className={styles.detailsTable}>
                  {account.Type === 'Bank Account' || account.Type === 'Interac' ? (
                    <Table {...getTableProps('accountInformation')} />
                  ) : (
                    <Table {...getTableProps('creditCardClientDetails')} />
                  )}
                </div>
              </Box>
            )}
            {account.Type === 'Bank Account' && (
              <>
                <hr className={styles.hr} />
                <Box mt="md">
                  <Table {...getTableProps('addressInformation')} />
                </Box>
              </>
            )}
            <hr className={styles.hr} />
            <Box mt="md">
              <Table {...getTableProps('defaultInformation')} />
            </Box>
            {account.DateAdded && (
              <Box mt="sm">
                <Description size="md" color="#8e8e8e" hasNoPadding>{`${t('description')} ${
                  account.DateAdded
                }`}</Description>
              </Box>
            )}
          </Box>
        }
      />
    </Box>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      accountInformation: () => {
        const accountInformation: vp.TableProps = {
          name: 'account-information',
          variant: 'detail',
          columns: {
            accountHolderName: { header: t('accountInformation.accountHolderName') || '', align: 'left' },
            accountNumber: {
              header: (
                <Box flx="row" ai="center" gap="sm">
                  <span>{t('accountInformation.accountNumber')}</span>
                  <Tooltip
                    type="hover"
                    information={
                      account.Type === 'Bank Account'
                        ? t('accountInformation.bankAccountNumberInfo')
                        : t('accountInformation.interacAccountNumberInfo')
                    }
                    position="right"
                    positionX="3px"
                    hasArrow={false}
                    icon="question"
                  />
                </Box>
              ),
              align: 'left',
            },
            accountType: { header: t('accountInformation.accountType') || '', align: 'left' },
            currency: { header: t('accountInformation.currency') || '', align: 'left' },
            country: { header: t('accountInformation.country') || '', align: 'left' },
          },
          rows: [
            {
              accountHolderName:
                account.Type === 'Bank Account' ? (
                  <Box pe="sm">{account.AccountHolderName}</Box>
                ) : (
                  <Box pe="sm">{account.RecipientName}</Box>
                ),
              accountNumber:
                account.Type === 'Bank Account' ? (
                  <Box flx="row" ai="center" pe="sm">
                    {account.BankId !== null && vp.hasBankImage(account.BankId || '') && (
                      <img
                        className={styles.logos}
                        alt="bank logo"
                        src={`https://embed-dev.vopay.com/images/logos-banks/${account.BankId || ''}.svg`}
                      />
                    )}
                    {account.Country === 'CA' && (
                      <span>
                        {`${account.FinancialInstitutionNumber || ''}-${account.BranchTransitNumber || ''}-${
                          account.AccountNumber || ''
                        }`}
                      </span>
                    )}
                    {account.Country === 'US' && (
                      <span>{`${account.ABARoutingNumber || ''}-${account.AccountNumber || ''}`}</span>
                    )}
                  </Box>
                ) : (
                  <Box flx="row" ai="center" pe="sm">
                    {account.RecipientEmailAddress || account.RecipientPhoneNumber}
                  </Box>
                ),
              accountType: tGlobal(`Accounts.MyBankAccounts.accountTypeItem.${account.Type || ' '}`),
              currency: account.Currency || '',
              country:
                account.Country === 'CA' ? t('accountInformation.countryCanada') : t('accountInformation.countryUS'),
            },
          ],
        };

        return accountInformation;
      },
      bankAccountInformation: () => {
        const accountInformation: vp.TableProps = {
          name: 'account-information',
          variant: 'detail',
          columns: {
            accountNumber: {
              header: (
                <Box flx="row" ai="center" gap="sm">
                  <span>{t('bankAccountInformation.accountNumber')}</span>
                  <Tooltip
                    type="hover"
                    information={
                      account.Type === 'Bank Account'
                        ? t('bankAccountInformation.bankAccountNumberInfo')
                        : t('bankAccountInformation.interacAccountNumberInfo')
                    }
                    position="right"
                    positionX="3px"
                    hasArrow={false}
                    icon="question"
                  />
                </Box>
              ),
              align: 'left',
            },
            accountType: { header: t('bankAccountInformation.accountType') || '', align: 'left' },
            currency: { header: t('bankAccountInformation.currency') || '', align: 'left' },
            country: { header: t('bankAccountInformation.country') || '', align: 'left' },
          },
          rows: [
            {
              accountNumber:
                account.Type === 'Bank Account' ? (
                  <Box flx="row" ai="center" pe="sm">
                    {account.BankId !== null && vp.hasBankImage(account.BankId || '') && (
                      <img
                        className={styles.logos}
                        alt="bank logo"
                        src={`https://embed-dev.vopay.com/images/logos-banks/${account.BankId || ''}.svg`}
                      />
                    )}
                    {account.Country === 'CA' && (
                      <span>
                        {`${account.FinancialInstitutionNumber || ''}-${account.BranchTransitNumber || ''}-${
                          account.AccountNumber || ''
                        }`}
                      </span>
                    )}
                    {account.Country === 'US' && (
                      <span>{`${account.ABARoutingNumber || ''}-${account.AccountNumber || ''}`}</span>
                    )}
                  </Box>
                ) : (
                  <Box flx="row" ai="center" pe="sm">
                    {account.RecipientEmailAddress || account.RecipientPhoneNumber}
                  </Box>
                ),
              accountType: tGlobal(`Accounts.MyBankAccounts.accountTypeItem.${account.Type || ' '}`),
              currency: account.Currency || '',
              country:
                account.Country === 'CA'
                  ? t('bankAccountInformation.countryCanada')
                  : t('bankAccountInformation.countryUS'),
            },
          ],
        };

        return accountInformation;
      },
      personalInformation: () => {
        const accountInformation: vp.TableProps = {
          name: 'personal-information',
          variant: 'detail',
          columns: {
            accountHolderName: { header: t('personalInformation.accountHolderName') || '', align: 'left' },
            contactNumber: { header: t('personalInformation.contactNumber') || '', align: 'left' },
            email: { header: t('personalInformation.email') || '', align: 'left' },
          },
          rows: [
            {
              accountHolderName:
                account.Type === 'Bank Account' ? (
                  <Box pe="sm">{account.AccountHolderName}</Box>
                ) : (
                  <Box pe="sm">{account.RecipientName}</Box>
                ),
              contactNumber: account.Phone || account.RecipientPhoneNumber || '',
              email: account.EmailAddress || account.RecipientEmailAddress || '',
            },
          ],
        };

        return accountInformation;
      },
      addressInformation: () => {
        const accountInformation: vp.TableProps = {
          name: 'address-information',
          variant: 'detail',
          columns: {
            address: { header: t('addressInformation.address') || '', align: 'left' },
            city: { header: t('addressInformation.city') || '', align: 'left' },
            province: { header: t('addressInformation.province') || '', align: 'left' },
            postalCode: { header: t('addressInformation.postalCode') || '', align: 'left' },
          },
          rows: [
            {
              address: account.Address || '',
              city: account.City || '',
              province: account.Province || '',
              postalCode: account.PostalCode || '',
            },
          ],
        };

        return accountInformation;
      },

      defaultInformation: () => {
        const defaultInformation: vp.TableProps = {
          name: 'default-information',
          variant: 'detail',
          columns: {
            isDefault: { header: t('defaultInformation.isDefault') || '', align: 'left' },
          },
          rows: [
            {
              isDefault: (
                <>
                  <Box flx="row" gap="sm" mt="sm" ai="center" width="auto">
                    {!(
                      account.IsDefault === '0' &&
                      !vp.toBool(account.IsOperationalDefault) &&
                      !vp.toBool(account.IsBillingDefault) &&
                      !vp.toBool(account.IsDefaultAccount)
                    ) && (
                      <Button
                        id="default-btn"
                        label={
                          vp.toBool(account.IsBillingDefault) && vp.toBool(account.IsOperationalDefault)
                            ? t('defaultInformation.defaultOperationalBilling')
                            : vp.toBool(account.IsBillingDefault) && vp.toBool(account.IsDefault)
                            ? t('defaultInformation.defaultBilling')
                            : vp.toBool(account.IsOperationalDefault)
                            ? t('defaultInformation.defaultOperational')
                            : t('defaultInformation.default')
                        }
                        textColor="#747474"
                        height="28px"
                        background="#FBFBFB"
                        borderColor="#c4c4c4"
                        textSize={0.8}
                        round
                        noShadow
                        isDisabled
                        fontWeight={400}
                      />
                    )}
                    {((account.IsDefault === '0' &&
                      !vp.toBool(account.IsOperationalDefault) &&
                      !vp.toBool(account.IsBillingDefault) &&
                      !vp.toBool(account.IsDefaultAccount)) ||
                      (((vp.toBool(account.IsOperationalDefault) && AccountListingType === 'Billing') ||
                        (vp.toBool(account.IsBillingDefault) && AccountListingType === 'Operational')) &&
                        !(vp.toBool(account.IsOperationalDefault) && vp.toBool(account.IsBillingDefault))) ||
                      (vp.toBool(account.IsBillingDefault) && !vp.toBool(account.IsDefault))) && (
                      <>
                        {account.IsDefault === '0' &&
                          !vp.toBool(account.IsOperationalDefault) &&
                          !vp.toBool(account.IsBillingDefault) &&
                          !vp.toBool(account.IsDefaultAccount) && <div>{t('defaultInformation.no')}</div>}
                        {(account.Type === 'Bank Account' ||
                          (account.Type === 'Credit Card' && AccountListingType === 'Billing')) && (
                          <Button
                            id="setDefaultButton"
                            label={
                              AccountListingType === 'Billing'
                                ? t('defaultInformation.setDefaultBilling')
                                : t('defaultInformation.setDefaultOperational')
                            }
                            color="secondary"
                            textSize={0.8}
                            height="28px"
                            onClick={() => {
                              setDefaultBank(account.Token || account.CreditCardToken || '', account.Type || '');
                            }}
                          />
                        )}
                      </>
                    )}
                  </Box>
                </>
              ),
            },
          ],
        };

        return defaultInformation;
      },
      creditCardClientDetails: () => {
        const creditCardDetails: vp.TableProps = {
          name: 'credit-card-account-information',
          variant: 'detail',
          columns: {
            accountHolderName: { header: t('creditCardClientDetails.accountHolderName') || '', align: 'left' },
            accountNumber: {
              header: (
                <Box flx="row" ai="center" gap="sm">
                  <span>{t('creditCardClientDetails.accountNumber')}</span>
                  <Tooltip
                    type="hover"
                    information={t('creditCardClientDetails.accountNumberTooltip')}
                    position="right"
                    hasArrow={false}
                    icon="question"
                  />
                </Box>
              ),
              align: 'left',
            },
            accountType: { header: t('creditCardClientDetails.accountType') || '', align: 'left' },
            expirationDate: { header: t('creditCardClientDetails.expirationDate') || '', align: 'left' },
            currency: { header: t('creditCardClientDetails.currency') || '', align: 'left' },
            country: { header: t('creditCardClientDetails.country') || '', align: 'left' },
          },
          rows: [
            {
              accountHolderName: account.CreditCardHolderName || '',
              accountNumber: (
                <Box flx="row" ai="center" gap="sm">
                  {account.CreditCardBrand !== null && (
                    <Icon
                      icon={
                        account.CreditCardBrand === 'mastercard'
                          ? 'cardMaster'
                          : account.CreditCardBrand === 'visa'
                          ? 'cardVisa'
                          : account.CreditCardBrand === 'amex'
                          ? 'cardAE'
                          : account.CreditCardBrand === 'discover'
                          ? 'cardDiscover'
                          : 'creditCard'
                      }
                      color="none"
                      size="xlg"
                      width="40px"
                    />
                  )}
                  <span>{getMaskedNumber(String(account.CreditCardNumber))}</span>
                </Box>
              ),
              accountType: tGlobal(`Accounts.MyBankAccounts.accountTypeItem.${account.Type || ' '}`),
              expirationDate: `${account.CreditCardExpiryMonth || ''} / ${account.CreditCardExpiryYear || ''}`,
              currency: account.Currency || '',
              country:
                account.Country === 'CA'
                  ? t('creditCardClientDetails.canada')
                  : t('creditCardClientDetails.unitedStates'),
            },
          ],
        };

        return creditCardDetails;
      },
    };

    return handlers[handler]();
  }

  function getMaskedNumber(cardNum: string) {
    const lastFourDigits = cardNum?.slice(-4);
    return `***${lastFourDigits}`;
  }
};

export default AccountDetails;
